import React from 'react';
import styled from 'styled-components';
import {Avatar, FlexColumn, FlexRow, Label, MultiCombobox} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {FieldLabel, SendQuoteContact} from './SendQuoteModal';

type Props = {
	contacts: SendQuoteContact[];
	to: string[];
	onToChange: (ids: string[]) => void;
};

const fullName = (contact: SendQuoteContact): string => `${contact.firstName || ''} ${contact.lastName || ''}`.trim();

/** The recipient's address, one type-step quieter than the name it follows. */
const ChipEmail = styled.span`
	color: ${props => props.theme.sys.color.text.content.auxiliary};
	white-space: nowrap;
`;

/**
 * The To field: selected contacts render as chips (avatar, name, quiet address, and the control's
 * own remove button), and the field itself is the picker for adding more. Recipients are chosen
 * from the client's contacts, never typed — an address the server would refuse cannot even be
 * asked for — so contacts without an address are not offered at all.
 */
export const RecipientFields = ({contacts, to, onToChange}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const items = contacts
		.filter(contact => !!contact.email)
		.map(contact => ({key: contact.id, textValue: `${fullName(contact)} ${contact.email}`, contact}));

	return (
		<FlexColumn gap="xs" data-cy="send-quote-to">
			<FieldLabel>{formatMessage({id: 'send_quote_modal.to'})}</FieldLabel>
			<MultiCombobox
				items={items}
				selectedKeys={to}
				setSelectedKeys={onToChange}
				width={MultiCombobox.WIDTH.FLEX}
				data-cy="send-quote-to"
			>
				{item => (
					<FlexRow gap="s" alignItems="center" wrap="nowrap">
						<Avatar name={fullName(item.contact)} size="s" />
						<Label noWrap>{fullName(item.contact)}</Label>
						<ChipEmail>{item.contact.email}</ChipEmail>
					</FlexRow>
				)}
			</MultiCombobox>
		</FlexColumn>
	);
};
